import { Card, Col, Typography } from "antd";
import React, { Fragment, useEffect } from "react";
import { FieldPathValue, FieldValues, Path, UseControllerProps, UseFormReturn } from "react-hook-form";
import { TemplateField } from "../../../types/template";
import { DateField, InputField, InputSwitch, ReactSelectField } from "../../atoms/FormElement";
import {
  FormRules,
  getInputPlaceholderMessage,
  getSelectPlaceholderMessage,
  getValidationProps,
} from "../../libs/form";
import { DATE_FORMATS, isSelectOption } from "../../libs/utils";
import { UploadDocument } from "../../molecules/Documents";
import { CheckboxWrapper } from "./CheckBoxWrapper";

type DynamicFieldsProps<T extends FieldValues> = {
  template: TemplateField[];
  control: UseFormReturn<T>["control"];
  errors: UseFormReturn<T>["formState"]["errors"];
  setValue: UseFormReturn<T>["setValue"];
  colSpan?: number;
};

export const DynamicFields = <T extends FieldValues>(props: DynamicFieldsProps<T>) => {
  const { template, control, errors, setValue, colSpan = 12 } = props;

  useEffect(() => {
    if (template?.length) {
      template.forEach((item) => {
        const fieldName = `dynamicFields.${item.name}` as Path<T>;
        const defaultValue = item?.default as FieldPathValue<T, typeof fieldName>;
        if (defaultValue !== undefined) {
          setValue(fieldName, defaultValue);
        }
      });
    }
  }, [template]);

  return (
    <>
      {template.map((field, idx) => {
        const fieldName = `dynamicFields.${field.name}`;
        const isDisabled = Boolean(field?.isDisabled);

        const { required, ...restRules } = field?.rules as UseControllerProps["rules"];
        const isRequired = Boolean(required);
        const rules = restRules as FormRules;

        switch (field.type) {
          case "text":
          case "number":
          case "email":
            return (
              <Col key={idx + fieldName} span={colSpan}>
                <InputField
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  setValue={setValue}
                  iProps={{
                    type: field.type,
                    min: field.min,
                    max: field.max,
                    placeholder: getInputPlaceholderMessage(field.label),
                  }}
                  disabled={isDisabled}
                  {...getValidationProps({ fieldName, errors, isRequired, rules })}
                />
              </Col>
            );

          case "select":
            return (
              <Col key={idx + fieldName} span={colSpan}>
                <ReactSelectField
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  setValue={setValue}
                  selectOption={
                    field.options?.map((opt) =>
                      isSelectOption(opt) ? { label: opt?.label, value: opt?.value } : { label: opt, value: opt },
                    ) || []
                  }
                  isDisabled={isDisabled}
                  {...getValidationProps({ fieldName, errors, isRequired, rules })}
                  iProps={{ placeholder: getSelectPlaceholderMessage(field.label) }}
                />
              </Col>
            );

          case "file":
            return (
              <Col key={idx + fieldName} span={colSpan}>
                <UploadDocument
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  disabled={isDisabled}
                  withPreview={false}
                  {...getValidationProps({ fieldName, errors, isRequired })}
                />
              </Col>
            );

          case "date":
            return (
              <Col key={idx + fieldName} span={colSpan}>
                <DateField
                  label={field.label}
                  fieldname={fieldName}
                  control={control}
                  setValue={setValue}
                  iProps={{ placeholder: DATE_FORMATS.display }}
                  disabled={isDisabled}
                  {...getValidationProps({ fieldName, errors, isRequired, rules })}
                />
              </Col>
            );

          case "checkbox":
            return (
              <Card key={idx + fieldName} title={field?.label || ""} style={{ marginInline: "0.5rem" }}>
                <CheckboxWrapper
                  parentLable={field?.label}
                  control={control}
                  idx={idx}
                  setValue={setValue}
                  options={field?.options}
                />
              </Card>
            );

          case "switch":
            return (
              <Col key={idx + fieldName} span={colSpan}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
                  <Typography.Text>{field?.label}</Typography.Text>
                  <InputSwitch
                    isRequired={false}
                    fieldname={fieldName}
                    label=""
                    control={control}
                    valueGot={field?.default}
                    setValue={setValue}
                    disabled={isDisabled}
                  />
                </div>
              </Col>
            );

          default:
            return <Fragment key={idx + fieldName} />;
        }
      })}
    </>
  );
};
